<template>
  <div class="filters">
    <div class="filters__ActiveListing">
      <template v-for="(item, key) in activeFilters">
        <div v-for="filter in item" class="button--Secondary small filters__ActiveItem">
          <template v-if="key === FilterIdentifiersObject.CATEGORY_RATING.backendId">
            <div class="filters__StarsWrapper">
              <div v-for="star in parseInt(filter)">
                <Icon name="star" :key="star" />
              </div>
            </div>
          </template>
          <template v-else>
            {{ getFilterText(key, filter) }}
          </template>
          <button @click="removeItem(key, filter)">
            <img src="/resources/images/icons/close.svg" width="16" height="16" />
          </button>
        </div>
      </template>
    </div>

    <div class="filters__ButtonWrapper">          
      <span>{{ $t('filters') }}</span>
      <button @click="open('filters')">
        <Icon name="filters" />
        <span v-if="numberOfResults">{{ numberOfResults }}</span>
      </button>
    </div>
  </div>

  <Modal v-model="dialogs.filters" class="modal__Filters">
    <div class="modal__FiltersInner">
      <h2>{{ $t('filters') }}</h2>

      <div class="modal__FiltersList">
        <div v-for="filterGroup in data" class="modal__FiltersGroup">
          <h3 v-text="filterGroup.title" />
          <div class="modal__FiltersGroupItems">
            <CheckboxField
              v-for="filter in filterGroup.items"
              :value="filter.id"
              :label="filter.name"
              v-model="activeFilters[filterGroup.identifier]"
              :disabled="filter.disabled"
              @change="changeCheckboxValue(filterGroup.identifier, filter.id)"
              :class="{'brand': filterGroup?.identifier === FilterIdentifiersObject.BRAND.backendId}"
            >
              <template v-if="(filter?.logo || filter?.image) && filterGroup.identifier !== FilterIdentifiersObject.BRAND.backendId">
                <Flare :srcset="filter.logo || filter.image" size="both" width="24" height="24" crop="fill" />
                {{ filter.name }}
              </template>
              <template v-else-if="filterGroup.identifier === FilterIdentifiersObject.BRAND.backendId">
                <Flare :srcset="filter.dark_logo" size="both" width="260" height="100" crop="fill" />
                {{ filter.name }}
              </template>
              <template v-else-if="filter.icon">
                <img :src="filter.icon" width="24" height="24" />
                {{ filter.name }}
              </template>
              <template v-else-if="filterGroup.identifier === FilterIdentifiersObject.CATEGORY_RATING.backendId">
                <div class="modal__FiltersStars">
                  <Icon name="star" v-for="star in filter.id" />
                </div>
              </template>
            </CheckboxField>
          </div>
        </div>
      </div>
    </div>
    
    <div class="modal__FiltersBottom buttons">
      <div>
        <button class="button__Text--Underline" @click="clearFilters">{{ $t('clearAll') }}</button>
      </div>
      <Button class="button--Primary" medium @click="close('filters')">{{ $t('confirm') }}</Button>
    </div>
  </Modal>
</template>

<script setup>
  import { FilterIdentifiersObject } from '~/utils/types';
  const route = useRoute();
  const router = useRouter();

  // Add this flag to track internal updates. If user lands with query params, then it is not an internal update.
  const isInternalUpdate = ref(false);

  const props = defineProps({
    data: {},
    modelValue: {},
    value: {},
    singleValue: {
      type: Boolean,
      default: false
    }
  });
  const emit = defineEmits(['close', 'update:modelValue', 'itemRemove', 'modalClose', 'routeChange']);

  const activeFilters = ref({});

  const dialogs = reactive({
    filters: false
  });
  const { open, close } = useModal(dialogs);

  onMounted(() => {
    setCleanFiltersData();
    setFiltersFromQuery();
  });

  const numberOfResults = computed(() => {
    return Object.values(activeFilters.value).reduce((total, filters) => total + filters.length, 0);
  });

  function clearFilters() {
    isInternalUpdate.value = true;
    setCleanFiltersData();
    setTimeout(() => {
      isInternalUpdate.value = false;
    }, 0);
  }

  // reset filers to initial state
  function setCleanFiltersData() {
    for (let filter of props.data) {
      activeFilters.value[filter.identifier] = [];
    }
  }

  function setFiltersFromQuery(customQuery) {
    // Set flag to prevent watcher from triggering route update
    isInternalUpdate.value = true;
    
    let query = route.query;
    if (customQuery) query = customQuery;
    if (!Object.keys(query).length) return;
    
    for (let item in query) {
      if (![null, undefined].includes(query[item]) && props.data.find(category => category.identifier === item)) {
        if (Array.isArray(query[item])) {
          activeFilters.value[item] = props.singleValue ? [parseQueryItemValue(query[item][0])] : query[item].map(item => parseQueryItemValue(item));
        } else {
          activeFilters.value[item] = [parseQueryItemValue(query[item])];
        }
      }
    }
    
    setTimeout(() => {
      isInternalUpdate.value = false;
    }, 10);
  }

  function parseQueryItemValue(item) {
    return isNaN(Number(item)) ? item : Number(item);
  }

  async function setQueryToRoute() {
    // Only proceed if not handling an internal update
    if (isInternalUpdate.value) return;
    
    // Set flag to prevent navigation guard from triggering
    isInternalUpdate.value = true;
    
    const filters = JSON.parse(JSON.stringify(activeFilters.value));
    let currentQuery = JSON.parse(JSON.stringify(route.query));

    for (let item in filters) {
      if (!filters?.[item]?.length && currentQuery[item]) delete currentQuery[item];
      else if (filters?.[item]?.length) currentQuery[item] = filters[item];
    }
    
    try {
      await router.replace({ query: currentQuery });
    } finally {
      setTimeout(() => {
        isInternalUpdate.value = false;
      }, 0);
    }
  }

  function removeItem(key, item) {
    if (!activeFilters.value[key]) return;
    const filters = JSON.parse(JSON.stringify(activeFilters.value));
    filters[key] = filters[key].filter(filter => filter !== item);
    activeFilters.value = filters;
    emit('itemRemove');
  }

  function getFilterText(key, item) {
    const category = props.data.find(item => item.identifier === key);
    if (category) {
      const element = category.items.find(element => element.id == item);
      return element ? element.name : '';
    }
  }

  // called @change in checkbox
  function changeCheckboxValue(field, value) {
    // console.log(activeFilters.value[field], value)
    // // console.log(activeFilters.value[field], value);
    // // if (!props.singleValue) {

    // // } else !activeFilters.value[field].length ? activeFilters.value[field] = [] : activeFilters.value[field] = [value];
    // if (!activeFilters.value[field].length) {
    //   activeFilters.value[field] = []
    // } else {
    //   if (!props.singleValue) {
    //     // console.log(value)
    //     // console.log(activeFilters.value[field]);
    //     // activeFilters.value[field].push(value)
    //   } else activeFilters.value[field] = [value]
    // }
  }

  watch(() => activeFilters.value, (newValue, oldValue) => {
    if (!isInternalUpdate.value) {
      setQueryToRoute();
    }
    emit('update:modelValue', newValue);
  }, { deep: true });

  watch(() => dialogs.filters, (value) => {
    if (!value) emit('close')
  });

  onBeforeRouteUpdate((to, from) => {
    if (isInternalUpdate.value) return;
    
    if (JSON.stringify(to.query) !== JSON.stringify(from.query)) {
      setFiltersFromQuery(to.query);
      emit('routeChange');
    }
  });
</script>

<style lang="scss">
  @import url('@/assets/styles/components/_filters.scss');
</style>